import { COMPANY, SITE_URL } from "@/config/company";

const SCHEMA_CONTEXT = "https://schema.org";

function areaServedNodes() {
  return COMPANY.areaServed.map((name) => ({
    "@type": "City" as const,
    name,
  }));
}

export function buildOrganizationSchema() {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: COMPANY.name,
    legalName: COMPANY.fullName,
    url: SITE_URL,
    logo: COMPANY.logoUrl,
    image: COMPANY.showroomImage,
    email: COMPANY.email,
    telephone: COMPANY.phoneTel,
    foundingDate: String(COMPANY.foundingYear),
    address: buildPostalAddress(),
    sameAs: [COMPANY.whatsappUrl, COMPANY.mapsUrl],
    areaServed: areaServedNodes(),
    knowsAbout: COMPANY.brands.flatMap((b) =>
      b.keywords.map((k) => `${b.name} ${k} Diyarbakır`)
    ),
  };
}

export function buildLocalBusinessSchema() {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
    "@id": `${SITE_URL}/#localbusiness`,
    name: COMPANY.name,
    description:
      "Diyarbakır Kayapınar'da Creavit vitrifiye, Duratiles seramik, QUA Granite, Kalekim ve Vitra Fix ürünlerinin bölge distribütörü ve showroom.",
    url: SITE_URL,
    logo: COMPANY.logoUrl,
    image: [COMPANY.showroomImage, COMPANY.logoUrl],
    telephone: COMPANY.phoneTel,
    email: COMPANY.email,
    priceRange: "$$",
    currenciesAccepted: "TRY",
    paymentAccepted: "Cash, Credit Card, Bank Transfer",
    address: buildPostalAddress(),
    geo: {
      "@type": "GeoCoordinates",
      latitude: COMPANY.lat,
      longitude: COMPANY.lng,
    },
    hasMap: COMPANY.mapsUrl,
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: COMPANY.openingHours.days,
      opens: COMPANY.openingHours.opens,
      closes: COMPANY.openingHours.closes,
    },
    areaServed: areaServedNodes(),
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
    sameAs: [COMPANY.whatsappUrl, COMPANY.mapsUrl],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Seramik, Vitrifiye ve Yapı Malzemeleri Kataloğu",
      itemListElement: COMPANY.brands.map((brand, i) => ({
        "@type": "OfferCatalog",
        position: i + 1,
        name: `${brand.name} — ${brand.role}`,
        url: `${SITE_URL}${brand.path}`,
      })),
    },
  };
}

export function buildWebSiteSchema() {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: COMPANY.name,
    url: SITE_URL,
    inLanguage: "tr-TR",
    publisher: { "@id": `${SITE_URL}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/markalar?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildHomeFAQSchema() {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "FAQPage",
    "@id": `${SITE_URL}/#faq`,
    mainEntity: [
      faq(
        "Diyarbakır'da Creavit vitrifiye bayisi nerede?",
        "Creavit vitrifiye ürünlerinin Diyarbakır bölge distribütörü Mayi Mimarlık'tır. Showroom adresimiz: Çeysa Plaza, Mezopotamya, 334. Sk. No:2A, Kayapınar/Diyarbakır."
      ),
      faq(
        "Diyarbakır'da seramik ve porselen karo nereden alınır?",
        "Duratiles seramik ve QUA Granite porselen karo ürünlerini Mayi Mimarlık showroomunda inceleyebilir, online katalogdan teklif alabilirsiniz. Diyarbakır Kayapınar Çeysa Plaza."
      ),
      faq(
        "Diyarbakır yapı kimyasalları ve seramik yapıştırıcı bayisi hangisi?",
        "Kalekim ve Vitra Fix yapı kimyasalları, seramik yapıştırıcı ve derz ürünleri için Mayi Mimarlık Diyarbakır ana bayisidir."
      ),
      faq(
        "Mayi Mimarlık showroom çalışma saatleri nedir?",
        "Pazartesi–Cumartesi 08:30–18:30 arası Kayapınar showroomumuzu ziyaret edebilirsiniz. Telefon: +90 533 329 95 93."
      ),
      faq(
        "Diyarbakır dışından teklif alabilir miyim?",
        "Evet. Güneydoğu Anadolu bölgesinde Diyarbakır, Mardin, Batman, Şanlıurfa ve çevre ilçelere proje teklifi ve ürün tedariki sağlıyoruz."
      ),
    ],
  };
}

export function buildBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildWebPageSchema(opts: {
  name: string;
  description: string;
  url: string;
  type?: string;
}) {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": opts.type || "WebPage",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    inLanguage: "tr-TR",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#localbusiness` },
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export function buildBrandCatalogSchema(opts: {
  brandName: string;
  description: string;
  url: string;
  productCount: number;
}) {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "CollectionPage",
    name: `${opts.brandName} Ürün Kataloğu — Diyarbakır`,
    description: opts.description,
    url: opts.url,
    inLanguage: "tr-TR",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: {
      "@type": "Brand",
      name: opts.brandName,
    },
    mainEntity: {
      "@type": "ItemList",
      name: `${opts.brandName} ürünleri`,
      numberOfItems: opts.productCount,
      itemListOrder: "https://schema.org/ItemListOrderDescending",
    },
    provider: { "@id": `${SITE_URL}/#localbusiness` },
  };
}

export function buildContactPageSchema() {
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "ContactPage",
    name: "İletişim — Mayi Mimarlık Diyarbakır",
    url: `${SITE_URL}/iletisim`,
    description: "Mayi Mimarlık Diyarbakır showroom iletişim bilgileri ve teklif formu.",
    mainEntity: { "@id": `${SITE_URL}/#localbusiness` },
  };
}

export function buildHomeGraphSchema() {
  const stripContext = (node: Record<string, unknown>) => {
    const { "@context": _c, ...rest } = node;
    return rest;
  };

  return {
    "@context": SCHEMA_CONTEXT,
    "@graph": [
      stripContext(buildOrganizationSchema()),
      stripContext(buildLocalBusinessSchema()),
      stripContext(buildWebSiteSchema()),
      stripContext(buildHomeFAQSchema()),
      stripContext(
        buildWebPageSchema({
          name: "Mayi Mimarlık — Diyarbakır Seramik & Yapı Malzemeleri",
          description:
            "Diyarbakır'da Creavit, Duratiles, QUA Granite, Kalekim ve Vitra Fix bölge distribütörü.",
          url: SITE_URL,
        })
      ),
    ],
  };
}

function buildPostalAddress() {
  return {
    "@type": "PostalAddress",
    streetAddress: COMPANY.streetAddress,
    addressLocality: COMPANY.district,
    addressRegion: COMPANY.city,
    postalCode: COMPANY.postalCode,
    addressCountry: COMPANY.country,
  };
}

function faq(question: string, answer: string) {
  return {
    "@type": "Question",
    name: question,
    acceptedAnswer: {
      "@type": "Answer",
      text: answer,
    },
  };
}

/** Statik HTML için tek parça JSON-LD (SSR'siz crawler desteği) */
export function buildStaticHeadSchemaJson(): string {
  return JSON.stringify(buildHomeGraphSchema());
}
